import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, api } from '../api/client';
import Modal from './Modal';
import NumericInput from './NumericInput';
import Button from './ui/Button';
import { ConfirmModal } from './ui';
import ToggleSwitch from './forms/ToggleSwitch';
import { segmentLabelText } from '../utils/segmentLabel';
import './MembershipMembersOverview.css';
import { translateError } from '../utils/errorMessages';

interface CalendarConfig {
  startMonth: number;
  intervals: Partial<Record<'yearly' | 'half_year' | 'quarter' | 'monthly', number>>;
}

interface MemberEnrollment {
  _id: string;
  name: string;
  color: string;
  paymentStatus: 'paid' | 'pending' | 'overdue' | 'not-enrolled' | null;
  enrollmentStatus: 'active' | 'paused' | 'expired' | 'removed' | null;
  periodStart?: string;
  periodEnd?: string;
  interval?: string;
  outstandingAmount?: number;
  autoRenew?: boolean;
  paidUntil?: string; // legacy fallback
  intentId?: number | null;
  // Season-division: the chosen stretch (which season + which segment).
  seasonId?: number | null;
  segmentScheme?: string | null;
  segmentIndex?: number | null;
}

interface GroupMembers {
  _id: string;
  name: string;
  members: MemberEnrollment[];
}

interface PriceTier {
  durationDays: number;
  price: number;
  label: string;
  sortOrder: number;
  isDefault: boolean;
}

interface MembershipMembersData {
  membershipId: string;
  membershipName?: string;
  yearlyPrice?: number;
  prices?: PriceTier[];
  groups: GroupMembers[];
  directMembers: MemberEnrollment[];
}

interface MembershipMembersOverviewProps {
  membershipId: string;
  yearlyPrice: number;
  membershipType?: 'season' | 'calendar' | 'time' | 'entry';
  calendarConfig?: CalendarConfig;
  authToken: string;
}

type IntervalKey = 'yearly' | 'half_year' | 'quarter' | 'monthly';

const INTERVAL_LABELS: Record<IntervalKey, string> = {
  yearly: 'memberships.intervalYearly',
  half_year: 'memberships.intervalHalfYear',
  quarter: 'memberships.intervalQuarter',
  monthly: 'memberships.intervalMonthly',
};

const INTERVAL_MONTHS: Record<IntervalKey, number> = {
  yearly: 12,
  half_year: 6,
  quarter: 3,
  monthly: 1,
};

const MembershipMembersOverview: React.FC<MembershipMembersOverviewProps> = ({
  membershipId,
  yearlyPrice,
  membershipType = 'time',
  calendarConfig,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<MembershipMembersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Enroll modal state
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberEnrollment | null>(null);
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [autoRenew, setAutoRenew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Time-bound enrollment state
  const [startDate, setStartDate] = useState('');
  const [selectedDurationDays, setSelectedDurationDays] = useState<number | null>(null);

  // Calendar-bound enrollment state
  const [selectedInterval, setSelectedInterval] = useState<IntervalKey | null>(null);

  // Add member modal state
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [allMembers, setAllMembers] = useState<Array<{ _id: number; name: string }>>([]);
  const [memberSearch, setMemberSearch] = useState('');

  // Pause / remove confirmation state
  const [pendingAction, setPendingAction] = useState<{ type: 'pause' | 'remove'; memberId: string } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Quick pay modal state
  const [quickPayModalOpen, setQuickPayModalOpen] = useState(false);
  const [quickPayMember, setQuickPayMember] = useState<MemberEnrollment | null>(null);
  const [quickPayAmount, setQuickPayAmount] = useState('');
  const [quickPayDate, setQuickPayDate] = useState('');
  const [quickPayLoading, setQuickPayLoading] = useState(false);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const { data: membersData } = await apiClient.get<MembershipMembersData>(
        `/api/memberships/${membershipId}/members`
      );
      setData(membersData);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (membershipId) {
      fetchMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipId]);

  const openEnrollModal = (member: MemberEnrollment) => {
    setSelectedMember(member);
    setStartDate(new Date().toISOString().split('T')[0]);
    setAutoRenew(member.autoRenew || false);
    setSelectedDurationDays(null);
    setSelectedInterval(null);
    setIsEditing(false);
    setEnrollModalOpen(true);
  };

  const openChangePeriodModal = (member: MemberEnrollment) => {
    setSelectedMember(member);
    setStartDate(new Date().toISOString().split('T')[0]);
    setAutoRenew(member.autoRenew || false);
    setIsEditing(true);

    // Pre-select current interval/tier
    if (membershipType === 'calendar' && member.interval) {
      setSelectedInterval(member.interval as IntervalKey);
      setSelectedDurationDays(null);
    } else {
      setSelectedInterval(null);
      setSelectedDurationDays(null);
    }

    setEnrollModalOpen(true);
  };

  const closeEnrollModal = () => {
    setEnrollModalOpen(false);
    setSelectedMember(null);
    setStartDate('');
    setAutoRenew(false);
    setSelectedDurationDays(null);
    setSelectedInterval(null);
    setIsEditing(false);
  };

  const fetchAllMembers = async () => {
    try {
      const { data } = await api.members.list();
      setAllMembers(data as Array<{ _id: number; name: string }>);
    } catch {
      // ignore
    }
  };

  const openAddMemberModal = () => {
    fetchAllMembers();
    setAddMemberModalOpen(true);
  };

  const closeAddMemberModal = () => {
    setAddMemberModalOpen(false);
    setMemberSearch('');
  };

  const getEnrolledMemberIds = (): Set<number> => {
    const ids = new Set<number>();
    if (!data) return ids;
    for (const group of data.groups) {
      for (const member of group.members) {
        ids.add(Number(member._id));
      }
    }
    for (const member of data.directMembers) {
      ids.add(Number(member._id));
    }
    return ids;
  };

  const handleSelectMemberToAdd = (member: { _id: number; name: string }) => {
    closeAddMemberModal();
    const newMember: MemberEnrollment = {
      _id: String(member._id),
      name: member.name,
      color: '',
      paymentStatus: null,
      enrollmentStatus: null,
    };
    openEnrollModal(newMember);
  };

  const handleTierSelect = (tier: PriceTier) => {
    setSelectedDurationDays(tier.durationDays);
  };

  const computePeriodPreview = (): string => {
    if (membershipType === 'calendar' && selectedInterval && calendarConfig) {
      const startMo = calendarConfig.startMonth;
      const months = INTERVAL_MONTHS[selectedInterval];
      const ref = new Date();
      const refYear = ref.getFullYear();

      // Generate candidate periods across 3 years (matching backend logic)
      const candidates: Array<{ year: number; month: number }> = [];
      for (let yearOffset = -1; yearOffset <= 1; yearOffset++) {
        const baseYear = refYear + yearOffset;
        for (let i = 0; i < 12; i += months) {
          let m = startMo + i;
          let y = baseYear;
          if (m > 12) { m -= 12; y += 1; }
          candidates.push({ year: y, month: m });
        }
      }

      // Find the period that contains today
      for (const cand of candidates) {
        const pStart = new Date(cand.year, cand.month - 1, 1);
        let endMonth = cand.month + months - 1;
        let endYear = cand.year;
        if (endMonth > 12) { endMonth -= 12; endYear += 1; }
        const pEnd = new Date(endYear, endMonth, 0); // last day of endMonth
        if (ref >= pStart && ref <= pEnd) {
          return `${pStart.toLocaleDateString()} – ${pEnd.toLocaleDateString()}`;
        }
      }
    }
    if (membershipType === 'time' && startDate && selectedDurationDays) {
      const start = new Date(startDate);
      const end = new Date(start);
      end.setDate(end.getDate() + selectedDurationDays - 1);
      return `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;
    }
    return '';
  };

  const handleEnrollMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMember) return;

    setEnrollLoading(true);
    try {
      if (isEditing) {
        // Change tier API
        const tierPayload: { interval?: string; durationDays?: number } = {};
        if (membershipType === 'calendar' && selectedInterval) {
          tierPayload.interval = selectedInterval;
        } else if (membershipType === 'time' && selectedDurationDays) {
          tierPayload.durationDays = selectedDurationDays;
        }
        await api.memberships.changeTier(membershipId, selectedMember._id, tierPayload);
      } else {
        // Enroll API
        const payload: any = {
          member: Number(selectedMember._id),
          autoRenew,
        };

        if (membershipType === 'calendar' && selectedInterval) {
          payload.interval = selectedInterval;
        } else if (membershipType === 'time') {
          if (startDate) payload.startDate = startDate;
          if (selectedDurationDays) payload.durationDays = selectedDurationDays;
        }

        await api.memberships.enrollMember(membershipId, payload);
      }
      closeEnrollModal();
      await fetchMembers();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setEnrollLoading(false);
    }
  };

  const handleResumeMember = async (memberId: string) => {
    try {
      await api.memberships.resumeMember(membershipId, memberId);
      await fetchMembers();
    } catch (err: any) {
      setError(translateError(err, t));
    }
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    setActionLoading(true);
    try {
      await api.memberships.removeMember(membershipId, pendingAction.memberId, pendingAction.type);
      await fetchMembers();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setActionLoading(false);
      setPendingAction(null);
    }
  };

  const openQuickPayModal = (member: MemberEnrollment) => {
    setQuickPayMember(member);
    setQuickPayAmount(member.outstandingAmount && member.outstandingAmount > 0 ? String(member.outstandingAmount) : '');
    setQuickPayDate(new Date().toISOString().split('T')[0]);
    setQuickPayModalOpen(true);
  };

  const closeQuickPayModal = () => {
    setQuickPayModalOpen(false);
    setQuickPayMember(null);
    setQuickPayAmount('');
    setQuickPayDate('');
  };

  const handleQuickPay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickPayMember) return;

    setQuickPayLoading(true);
    try {
      await api.payments.create({
        member: Number(quickPayMember._id),
        membership: Number(membershipId),
        amount: parseFloat(quickPayAmount),
        date: quickPayDate,
        description: data?.membershipName || '',
      });
      closeQuickPayModal();
      await fetchMembers();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setQuickPayLoading(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPaymentStatusBadge = (status: MemberEnrollment['paymentStatus']) => {
    switch (status) {
      case 'paid':
        return <span className="membership-badge membership-paid">{t('memberships.paymentPaid')}</span>;
      case 'pending':
        return <span className="membership-badge membership-expiring">{t('memberships.paymentPending')}</span>;
      case 'overdue':
        return <span className="membership-badge membership-expired">{t('memberships.paymentOverdue')}</span>;
      case 'not-enrolled':
      default:
        return <span className="membership-badge membership-not-enrolled">{t('memberships.statusNotEnrolled')}</span>;
    }
  };

  const getEnrollmentStatusBadge = (status: MemberEnrollment['enrollmentStatus']) => {
    switch (status) {
      case 'active':
        return <span className="membership-badge membership-paid">{t('memberships.statusActive')}</span>;
      case 'paused':
        return <span className="membership-badge membership-expiring">{t('memberships.statusPaused')}</span>;
      case 'expired':
        return <span className="membership-badge membership-expired">{t('memberships.statusExpiredEnrollment')}</span>;
      case 'removed':
        return <span className="membership-badge membership-not-enrolled">{t('memberships.statusRemoved')}</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatPeriod = (member: MemberEnrollment) => {
    if (member.periodStart && member.periodEnd) {
      return `${formatDate(member.periodStart)} – ${formatDate(member.periodEnd)}`;
    }
    // Legacy fallback
    if (member.paidUntil) {
      return `${t('memberships.paidUntil')}: ${formatDate(member.paidUntil)}`;
    }
    return '';
  };

  const renderMemberRow = (member: MemberEnrollment) => (
    <div key={member._id} className="membership-member">
      <div className="member-info">
        <span className="member-name">{member.name}</span>
        <div className="member-meta">
          {member.enrollmentStatus && getEnrollmentStatusBadge(member.enrollmentStatus)}
          {getPaymentStatusBadge(member.paymentStatus)}
          {segmentLabelText(t, member) && (
            <span className="mm-segment-tag">{segmentLabelText(t, member)}</span>
          )}
          {(member.periodStart || member.paidUntil) && (
            <span className="member-paid-until">{formatPeriod(member)}</span>
          )}
        </div>
      </div>
      <div className="member-actions">
        {(member.enrollmentStatus === 'active' || member.enrollmentStatus === 'paused') && (
          <button
            type="button"
            className="mm-action-btn mm-action-btn--primary"
            onClick={(e) => { e.stopPropagation(); openChangePeriodModal(member); }}
            title={t('memberships.changePeriod')}
          >
            <i className="fas fa-edit" aria-hidden="true"></i>
          </button>
        )}
        {member.enrollmentStatus === 'active' && (
          <button
            type="button"
            className="mm-action-btn mm-action-btn--warning"
            onClick={(e) => { e.stopPropagation(); setPendingAction({ type: 'pause', memberId: member._id }); }}
            title={t('memberships.pause')}
          >
            <i className="fas fa-pause" aria-hidden="true"></i>
          </button>
        )}
        {member.enrollmentStatus === 'paused' && (
          <button
            type="button"
            className="mm-action-btn mm-action-btn--success"
            onClick={(e) => { e.stopPropagation(); handleResumeMember(member._id); }}
            title={t('memberships.resume')}
          >
            <i className="fas fa-play" aria-hidden="true"></i>
          </button>
        )}
        {(member.paymentStatus === 'pending' || member.paymentStatus === 'overdue') && (
          <button
            type="button"
            className="mm-action-btn mm-action-btn--pay"
            onClick={(e) => { e.stopPropagation(); openQuickPayModal(member); }}
            title={t('memberships.quickPay')}
          >
            💰
          </button>
        )}
        {(member.enrollmentStatus === 'active' || member.enrollmentStatus === 'paused') && (
          <button
            type="button"
            className="mm-action-btn mm-action-btn--danger"
            onClick={(e) => { e.stopPropagation(); setPendingAction({ type: 'remove', memberId: member._id }); }}
            title={t('memberships.removeEnrollment')}
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        )}
        {(!member.enrollmentStatus || member.enrollmentStatus === 'expired' || member.enrollmentStatus === 'removed') && (
          <button
            type="button"
            className="mm-action-btn mm-action-btn--primary"
            onClick={(e) => { e.stopPropagation(); openEnrollModal(member); }}
            title={t('memberships.enrollButton')}
          >
            <i className="fas fa-plus" aria-hidden="true"></i>
          </button>
        )}
      </div>
    </div>
  );

  const getAvailableIntervals = (): Array<{ key: IntervalKey; price: number }> => {
    if (!calendarConfig?.intervals) return [];
    return (Object.entries(calendarConfig.intervals) as Array<[IntervalKey, number]>)
      .filter(([, price]) => price > 0)
      .map(([key, price]) => ({ key, price }));
  };

  if (loading) {
    return <div className="membership-overview loading">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="membership-overview error">{t('common.error')}: {error}</div>;
  }

  if (!data) {
    return <div className="membership-overview empty">{t('common.noResults')}</div>;
  }

  const hasGroups = data.groups.length > 0;
  const hasDirectMembers = data.directMembers.length > 0;

  if (!hasGroups && !hasDirectMembers) {
    return (
      <div className="membership-overview empty">
        <p>{t('memberships.noMembersAssigned')}</p>
      </div>
    );
  }

  const periodPreview = computePeriodPreview();

  return (
    <div className="membership-overview">
      <div className="membership-header-row">
        <h4 className="membership-title">{t('memberships.membersTitle')}</h4>
        <Button onClick={openAddMemberModal}>
          {t('memberships.addMemberToMembership')}
        </Button>
      </div>
      <p className="membership-price">
        {membershipType === 'time'
          ? <>{t('memberships.yearlyPrice')}: <strong>{yearlyPrice.toFixed(2)}</strong></>
          : <>{t('memberships.typeCalendar')}</>
        }
      </p>

      {hasGroups && (
        <div className="membership-groups">
          {data.groups.map((group) => (
            <div key={group._id} className="membership-group">
              <div className="group-header">
                <span className="group-name">{group.name}</span>
                <span className="group-count">({group.members.length})</span>
              </div>
              <div className="group-members">
                {group.members.map(renderMemberRow)}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasDirectMembers && (
        <div className="membership-direct">
          <div className="direct-header">{t('memberships.directMembers')}</div>
          <div className="direct-members">
            {data.directMembers.map(renderMemberRow)}
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      <Modal
        isOpen={addMemberModalOpen}
        onClose={closeAddMemberModal}
        title={t('memberships.addMemberToMembership')}
        size="medium"
      >
        <div className="add-member-search">
          <input
            type="text"
            placeholder={t('memberships.searchMembers')}
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="add-member-list">
          {(() => {
            const enrolledIds = getEnrolledMemberIds();
            const filtered = allMembers
              .filter(m => m.name.toLowerCase().includes(memberSearch.toLowerCase()))
              .slice(0, 20);

            return filtered.map(member => {
              const isEnrolled = enrolledIds.has(member._id);
              return (
                <div
                  key={member._id}
                  className={`add-member-item ${isEnrolled ? 'enrolled' : ''}`}
                  onClick={() => !isEnrolled && handleSelectMemberToAdd(member)}
                >
                  <span className="add-member-name">{member.name}</span>
                  {isEnrolled && (
                    <span className="add-member-enrolled">{t('memberships.alreadyEnrolled')}</span>
                  )}
                </div>
              );
            });
          })()}
        </div>
      </Modal>

      {/* Quick Pay Modal */}
      <Modal
        isOpen={quickPayModalOpen}
        onClose={closeQuickPayModal}
        title={t('memberships.quickPay')}
        size="small"
      >
        {quickPayMember && (
          <form onSubmit={handleQuickPay} className="enroll-modal-form">
            <div className="enroll-modal-member">
              <strong>{quickPayMember.name}</strong>
              {quickPayMember.outstandingAmount && quickPayMember.outstandingAmount > 0 && (
                <div style={{ marginTop: 4, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  {t('memberships.outstanding')}: {formatAmount(quickPayMember.outstandingAmount)} CZK
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="qp-amount">{t('payments.amount')}</label>
              <NumericInput
                id="qp-amount"
                value={quickPayAmount}
                onChange={(e) => setQuickPayAmount(e.target.value)}
                step="1"
                min="0"
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="qp-date">{t('payments.date')}</label>
              <input
                type="date"
                id="qp-date"
                value={quickPayDate}
                onChange={(e) => setQuickPayDate(e.target.value)}
                required
              />
            </div>
            <div className="enroll-modal-actions">
              <Button type="submit" fullWidth loading={quickPayLoading}>
                {quickPayLoading ? t('common.saving') : t('payments.addPayment')}
              </Button>
              <Button variant="secondary" fullWidth onClick={closeQuickPayModal}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Enroll / Change Period Modal */}
      <Modal
        isOpen={enrollModalOpen}
        onClose={closeEnrollModal}
        title={isEditing ? t('memberships.changePeriodTitle') : t('memberships.enrollMember')}
        size="medium"
      >
        {selectedMember && (
          <form onSubmit={handleEnrollMember} className="enroll-modal-form">
            <div className="enroll-modal-member">
              <strong>{selectedMember.name}</strong>
            </div>

            {membershipType === 'calendar' ? (
              <>
                <div className="form-group">
                  <label>{t('memberships.selectInterval')}</label>
                  <div className="radio-cards">
                    {getAvailableIntervals().map(({ key, price }) => (
                      <label
                        key={key}
                        className={`radio-card${selectedInterval === key ? ' selected' : ''}`}
                        onClick={() => setSelectedInterval(key)}
                      >
                        <input
                          type="radio"
                          name="interval"
                          value={key}
                          checked={selectedInterval === key}
                          onChange={() => setSelectedInterval(key)}
                        />
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between' }}>
                          <span>{t(INTERVAL_LABELS[key])}</span>
                          <span>{price.toLocaleString()} CZK</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {periodPreview && (
                  <div className="form-group">
                    <label>{t('memberships.periodPreview')}</label>
                    <div style={{ padding: '8px 12px', background: 'var(--bg-dark)', borderRadius: '4px', fontSize: '0.9rem' }}>
                      {periodPreview}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {!isEditing && (
                  <div className="form-group">
                    <label htmlFor="modal-start-date">{t('memberships.selectStartDate')}</label>
                    <input
                      type="date"
                      id="modal-start-date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  </div>
                )}

                {data?.prices && data.prices.length > 0 ? (
                  <div className="quick-select-buttons">
                    {data.prices
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((tier, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`quick-select-btn${selectedDurationDays === tier.durationDays ? ' selected' : ''}`}
                          onClick={() => handleTierSelect(tier)}
                          style={selectedDurationDays === tier.durationDays ? { borderColor: 'var(--primary-color)', background: 'var(--bg-medium)' } : undefined}
                        >
                          {tier.label} - {tier.price.toLocaleString()}
                        </button>
                      ))}
                  </div>
                ) : (
                  <div className="quick-select-buttons">
                    <button type="button" className="quick-select-btn" onClick={() => handleTierSelect({ durationDays: 180, price: yearlyPrice / 2, label: t('memberships.halfYear'), sortOrder: 1, isDefault: false })}>
                      {t('memberships.halfYear')}
                    </button>
                    <button type="button" className="quick-select-btn" onClick={() => handleTierSelect({ durationDays: 365, price: yearlyPrice, label: t('memberships.fullYear'), sortOrder: 2, isDefault: true })}>
                      {t('memberships.fullYear')}
                    </button>
                  </div>
                )}

                {periodPreview && (
                  <div className="form-group">
                    <label>{t('memberships.periodPreview')}</label>
                    <div style={{ padding: '8px 12px', background: 'var(--bg-dark)', borderRadius: '4px', fontSize: '0.9rem' }}>
                      {periodPreview}
                    </div>
                  </div>
                )}
              </>
            )}

            {!isEditing && (
              <div className="form-group" style={{ marginTop: '0.5rem' }}>
                <div className="interval-row">
                  <ToggleSwitch checked={autoRenew} onChange={setAutoRenew} />
                  <span className="interval-row__name">{t('memberships.autoRenew')}</span>
                </div>
                <small className="auto-renew-desc">{t('memberships.autoRenewDesc')}</small>
              </div>
            )}

            <div className="enroll-modal-actions">
              <Button type="submit" fullWidth loading={enrollLoading}>
                {enrollLoading ? t('common.saving') : (isEditing ? t('memberships.changePeriod') : t('memberships.enrollConfirm'))}
              </Button>
              <Button variant="secondary" fullWidth onClick={closeEnrollModal}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
        title={t('common.confirmTitle')}
        message={pendingAction?.type === 'remove' ? t('memberships.confirmRemove') : t('memberships.confirmPause')}
        confirmText={pendingAction?.type === 'remove' ? t('memberships.removeEnrollment') : t('memberships.pause')}
        cancelText={t('common.cancel')}
        variant={pendingAction?.type === 'remove' ? 'danger' : 'default'}
        loading={actionLoading}
      />
    </div>
  );
};

export default MembershipMembersOverview;
